import { useState } from "react";
import { Search, Zap, Building2, TrendingUp, ExternalLink, AlertCircle, CheckCircle2, Eye, EyeOff, X, FileText, Sparkles } from "lucide-react";
import { supabase } from "./supabaseClient";

function IntegrationField({ label, value, onChange, connected, onClear }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label} API Key</label>
        {connected && (
          <span className="flex items-center gap-1 text-[9px] font-black text-green-600 uppercase tracking-widest">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </span>
        )}
      </div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full p-4 pr-20 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-mono text-xs transition"
          placeholder="Paste your API key here..."
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <button onClick={() => setShow(!show)} className="p-1.5 hover:bg-gray-200 rounded-lg transition text-gray-400">
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          {value && (
            <button onClick={onClear} className="p-1.5 hover:bg-gray-200 rounded-lg transition text-gray-400">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyCard({ company }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-red-200 transition shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center font-black text-gray-700 text-sm flex-shrink-0">
              {company.name?.slice(0, 2).toUpperCase() || "??"}
            </div>
            <div>
              <p className="font-black text-gray-900 tracking-tight">{company.name || "Unknown"}</p>
              <p className="text-[10px] font-bold text-gray-400">{company.location || "Location N/A"}</p>
            </div>
          </div>
          {company.website && (
            <a href={`https://${company.website}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-gray-100 rounded-xl transition">
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>
          )}
        </div>
        <p className="text-sm text-gray-600 font-medium leading-relaxed mb-4 line-clamp-2">{company.description || "No description available."}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {company.categories?.slice(0, 3).map((cat, i) => (
            <span key={i} className="px-2 py-1 bg-red-50 text-red-600 rounded-lg text-[10px] font-black uppercase tracking-widest">{cat}</span>
          ))}
          {company.stage && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-black uppercase tracking-widest">{company.stage}</span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Founded", value: company.founded || "N/A" },
            { label: "Employees", value: company.employees || "N/A" },
            { label: "Total Raised", value: company.totalFunding ? `$${(company.totalFunding / 1000000).toFixed(1)}M` : "N/A" },
          ].map((m, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-2 text-center">
              <p className="text-[8px] font-black text-gray-400 uppercase">{m.label}</p>
              <p className="text-xs font-black text-gray-900 mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>
      </div>
      {company.fundingRounds?.length > 0 && (
        <div className="border-t border-gray-50">
          <button onClick={() => setExpanded(!expanded)} className="w-full px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest hover:bg-gray-50 transition flex items-center justify-between">
            <span>{company.fundingRounds.length} Funding Rounds</span>
            <TrendingUp className="w-3 h-3" />
          </button>
          {expanded && (
            <div className="px-6 pb-4 space-y-2">
              {company.fundingRounds.slice(0, 5).map((round, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-xs font-black text-gray-900">{round.type || "Unknown Round"}</p>
                    <p className="text-[10px] text-gray-400 font-bold">{round.date || ""}</p>
                  </div>
                  <p className="text-sm font-black text-red-600">{round.amount ? `$${(round.amount / 1000000).toFixed(1)}M` : "Undisclosed"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {company.investors?.length > 0 && (
        <div className="px-6 pb-4 border-t border-gray-50 pt-3">
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Investors</p>
          <div className="flex flex-wrap gap-1.5">
            {company.investors.slice(0, 4).map((inv, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-bold">{inv}</span>
            ))}
            {company.investors.length > 4 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-400 rounded-lg text-[10px] font-bold">+{company.investors.length - 4} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HarmonicCard({ company }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-red-200 transition shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center font-black text-gray-700 text-sm flex-shrink-0">
              {(company.name || "??").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p className="font-black text-gray-900 tracking-tight">{company.name || "Unknown"}</p>
              {company.website && (
                <p className="text-[10px] font-bold text-gray-400">{company.website.replace(/^https?:\/\//, "")}</p>
              )}
            </div>
          </div>
          {company.website && (
            <a href={company.website} target="_blank" rel="noreferrer" className="p-2 hover:bg-gray-100 rounded-xl transition">
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>
          )}
        </div>
        {company.description && (
          <p className="text-sm text-gray-600 font-medium leading-relaxed mb-4 line-clamp-2">{company.description}</p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded-xl p-2 text-center">
            <p className="text-[8px] font-black text-gray-400 uppercase">Headcount</p>
            <p className="text-xs font-black text-gray-900 mt-0.5">{company.headcount ?? "N/A"}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-2 text-center">
            <p className="text-[8px] font-black text-gray-400 uppercase">Latest Round</p>
            <p className="text-xs font-black text-gray-900 mt-0.5">
              {company.latestRound || "N/A"}
              {company.latestRoundAmount ? ` · $${(company.latestRoundAmount / 1e6).toFixed(1)}M` : ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchCard({ company, match }) {
  const scoreColor = match.score >= 80 ? "text-green-600 bg-green-50 border-green-200" : match.score >= 60 ? "text-yellow-600 bg-yellow-50 border-yellow-200" : "text-red-600 bg-red-50 border-red-200";
  const barColor = match.score >= 80 ? "bg-green-500" : match.score >= 60 ? "bg-yellow-500" : "bg-red-400";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-red-200 transition shadow-sm overflow-hidden">
      <div className="p-6">
        {/* Header with score */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center font-black text-gray-700 text-sm flex-shrink-0">
              {company.name?.slice(0, 2).toUpperCase() || "??"}
            </div>
            <div>
              <p className="font-black text-gray-900 tracking-tight">{company.name || "Unknown"}</p>
              <p className="text-[10px] font-bold text-gray-400">{company.location || "Location N/A"}</p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-xl border text-sm font-black ${scoreColor}`}>
            {match.score}% match
          </div>
        </div>

        {/* Match bar */}
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
          <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${match.score}%` }} />
        </div>

        {/* AI reasoning */}
        <div className="bg-gray-50 rounded-2xl p-4 mb-4">
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">AI Analysis</p>
          <p className="text-xs font-medium text-gray-700 leading-relaxed">{match.reasoning}</p>
        </div>

        {/* Criteria checks */}
        <div className="space-y-2 mb-4">
          {match.criteria?.map((c, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`text-xs font-black flex-shrink-0 ${c.met ? "text-green-500" : "text-red-400"}`}>{c.met ? "✓" : "✗"}</span>
              <p className={`text-xs font-medium ${c.met ? "text-gray-700" : "text-gray-400"}`}>{c.label}</p>
            </div>
          ))}
        </div>

        {/* Company details */}
        <div className="flex flex-wrap gap-2 mb-3">
          {company.categories?.slice(0, 3).map((cat, i) => (
            <span key={i} className="px-2 py-1 bg-red-50 text-red-600 rounded-lg text-[10px] font-black uppercase tracking-widest">{cat}</span>
          ))}
          {company.stage && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-black uppercase tracking-widest">{company.stage}</span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Founded", value: company.founded || "N/A" },
            { label: "Employees", value: company.employees || "N/A" },
            { label: "Total Raised", value: company.totalFunding ? `$${(company.totalFunding / 1000000).toFixed(1)}M` : "N/A" },
          ].map((m, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-2 text-center">
              <p className="text-[8px] font-black text-gray-400 uppercase">{m.label}</p>
              <p className="text-xs font-black text-gray-900 mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {company.website && (
        <div className="px-6 pb-4">
          <a href={`https://${company.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-bold text-red-600 hover:underline">
            <ExternalLink className="w-3 h-3" /> Visit website
          </a>
        </div>
      )}
    </div>
  );
}

async function scoreCompaniesAgainstThesis(thesis, companies) {
  const companySummaries = companies.map((c, i) => `
Company ${i + 1}: ${c.name}
- Description: ${c.description || "N/A"}
- Location: ${c.location || "N/A"}
- Stage: ${c.stage || "N/A"}
- Founded: ${c.founded || "N/A"}
- Employees: ${c.employees || "N/A"}
- Total Funding: ${c.totalFunding ? `$${(c.totalFunding / 1000000).toFixed(1)}M` : "N/A"}
- Categories: ${c.categories?.join(", ") || "N/A"}
- Investors: ${c.investors?.slice(0, 3).join(", ") || "N/A"}
  `).join("\n");

  const prompt = `You are an expert VC analyst. Score each company against the investment thesis below.

INVESTMENT THESIS:
${thesis}

COMPANIES TO EVALUATE:
${companySummaries}

Return ONLY a valid JSON array with no markdown. One object per company in the same order:
[
  {
    "score": 85,
    "reasoning": "2-3 sentence explanation of why this company matches or doesn't match the thesis",
    "criteria": [
      { "label": "specific criterion from thesis", "met": true },
      { "label": "another criterion", "met": false }
    ]
  }
]

Score from 0-100. Be strict and honest. Extract 3-4 specific criteria from the thesis for each company.`;

  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch("/api/analyze", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session?.access_token || "anonymous"}`
    },
    body: JSON.stringify({ text: prompt, criteria: "" })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Analysis failed");
  const text = data.text;
  const clean = text.replace(/```json|```/g, "").trim();
  const start = clean.indexOf("[");
  const end = clean.lastIndexOf("]");
  return JSON.parse(clean.slice(start, end + 1));
}

export default function MarketResearch({ apiKeys, setApiKeys }) {
  const [mode, setMode] = useState("search"); // "search" | "thesis"
  const [researchQuery, setResearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsSource, setResultsSource] = useState("");
  const [error, setError] = useState("");
  const [activeSource, setActiveSource] = useState("crunchbase");
  const [localKeys, setLocalKeys] = useState({
    crunchbase: apiKeys?.crunchbase || "",
    tracxn: apiKeys?.tracxn || "",
    dealroom: apiKeys?.dealroom || "",
    harmonic: localStorage.getItem("harmonic_api_key") || apiKeys?.harmonic || "",
  });

  // Thesis matcher state
  const [thesis, setThesis] = useState("");
  const [thesisQuery, setThesisQuery] = useState("");
  const [isMatching, setIsMatching] = useState(false);
  const [matchResults, setMatchResults] = useState([]);
  const [matchError, setMatchError] = useState("");
  const [matchStep, setMatchStep] = useState(""); // status message

  const saveKeys = () => {
    if (localKeys.harmonic) localStorage.setItem("harmonic_api_key", localKeys.harmonic);
    else localStorage.removeItem("harmonic_api_key");
    setApiKeys({ ...apiKeys, ...localKeys });
  };

  const fetchFromCrunchbase = async (query, key) => {
    const searchRes = await fetch(
      `https://api.crunchbase.com/api/v4/autocompletes?query=${encodeURIComponent(query)}&collection_ids=organizations&limit=10&user_key=${key}`
    );
    if (!searchRes.ok) {
      if (searchRes.status === 401) throw new Error("Invalid Crunchbase API key.");
      if (searchRes.status === 429) throw new Error("API rate limit reached. Please wait.");
      throw new Error("Crunchbase search failed.");
    }
    const searchData = await searchRes.json();
    const entities = searchData.entities || [];
    const detailed = await Promise.all(
      entities.slice(0, 8).map(async (entity) => {
        try {
          const detailRes = await fetch(
            `https://api.crunchbase.com/api/v4/entities/organizations/${entity.identifier?.permalink}?user_key=${key}&field_ids=short_description,location_identifiers,website_url,founded_on,num_employees_enum,total_funding_usd,funding_rounds,investor_identifiers,categories,last_funding_type`
          );
          if (!detailRes.ok) return null;
          const detail = await detailRes.json();
          const props = detail.properties || {};
          return {
            name: entity.identifier?.value,
            description: props.short_description,
            location: props.location_identifiers?.[0]?.value,
            website: props.website_url?.replace("https://", "").replace("http://", ""),
            founded: props.founded_on?.value?.slice(0, 4),
            employees: props.num_employees_enum,
            totalFunding: props.total_funding_usd,
            stage: props.last_funding_type,
            categories: props.categories?.map(c => c.value) || [],
            investors: props.investor_identifiers?.map(i => i.value) || [],
            fundingRounds: props.funding_rounds?.map(r => ({
              type: r.investment_type,
              amount: r.raised_amount_usd,
              date: r.announced_on,
            })) || [],
          };
        } catch { return null; }
      })
    );
    return detailed.filter(Boolean);
  };

  const fetchFromHarmonic = async (query, key) => {
    const res = await fetch(
      `https://api.harmonic.ai/companies?q=${encodeURIComponent(query)}`,
      { headers: { apikey: key, "Content-Type": "application/json" } }
    );
    if (!res.ok) throw new Error("harmonic_error");
    const data = await res.json();
    const raw = data.results || data.companies || (Array.isArray(data) ? data : []);
    return raw.map(c => ({
      name: c.name || c.company_name,
      website: c.website_url || c.website,
      description: c.description || c.long_description,
      headcount: c.headcount || c.employee_count || c.num_employees,
      latestRound: c.last_funding_type || c.latest_funding_stage || c.funding?.last_round_type,
      latestRoundAmount: c.last_funding_amount || c.funding?.last_round_amount,
    }));
  };

  const handleSearch = async () => {
    if (!researchQuery.trim()) return;
    setError(""); setResults([]);

    if (activeSource === "harmonic") {
      if (!localKeys.harmonic) { setError("Add your Harmonic API key above to search company intelligence"); return; }
      setIsSearching(true);
      try {
        const companies = await fetchFromHarmonic(researchQuery, localKeys.harmonic);
        setResults(companies);
        setResultsSource("harmonic");
      } catch {
        setError("Could not connect to Harmonic. Please check your API key.");
      } finally {
        setIsSearching(false);
      }
      return;
    }

    const key = localKeys.crunchbase;
    if (!key) { setError("Please enter your Crunchbase API key first."); return; }
    if (activeSource !== "crunchbase") { setError(`${activeSource} integration coming soon!`); return; }
    setIsSearching(true);
    try {
      const companies = await fetchFromCrunchbase(researchQuery, key);
      setResults(companies);
      setResultsSource("crunchbase");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleThesisMatch = async () => {
    if (!thesis.trim()) { setMatchError("Please enter your investment thesis."); return; }
    if (!thesisQuery.trim()) { setMatchError("Please enter a search term to find companies."); return; }
    if (!localKeys.crunchbase) { setMatchError("Please enter your Crunchbase API key first."); return; }

    setMatchError(""); setMatchResults([]); setIsMatching(true);

    try {
      setMatchStep("Searching Crunchbase for companies...");
      const companies = await fetchFromCrunchbase(thesisQuery, localKeys.crunchbase);

      if (companies.length === 0) {
        setMatchError("No companies found. Try a different search term.");
        setIsMatching(false);
        return;
      }

      setMatchStep(`Found ${companies.length} companies. Running AI thesis analysis...`);
      const scores = await scoreCompaniesAgainstThesis(thesis, companies);

      const combined = companies.map((c, i) => ({
        company: c,
        match: scores[i] || { score: 0, reasoning: "Could not score.", criteria: [] }
      }));

      combined.sort((a, b) => b.match.score - a.match.score);
      setMatchResults(combined);
      setMatchStep("");
    } catch (err) {
      setMatchError(err.message || "Something went wrong.");
    } finally {
      setIsMatching(false);
    }
  };

  const crunchbaseConnected = !!localKeys.crunchbase;
  const tracxnConnected = !!localKeys.tracxn;
  const dealroomConnected = !!localKeys.dealroom;
  const harmonicConnected = !!localKeys.harmonic;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">

      {/* Left — API Keys */}
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-black mb-1 tracking-tight flex items-center gap-2">
            <Zap className="w-5 h-5 text-red-600" /> Connect your data sources
          </h3>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Bring your own API keys.</p>
          <p className="text-[10px] font-medium text-gray-400 mb-6">Your keys stay in your browser only.</p>
          <div className="space-y-5">
            <IntegrationField label="Crunchbase" value={localKeys.crunchbase} onChange={(v) => setLocalKeys({ ...localKeys, crunchbase: v })} connected={crunchbaseConnected} onClear={() => setLocalKeys({ ...localKeys, crunchbase: "" })} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Harmonic API Key</label>
                {harmonicConnected && (
                  <span className="flex items-center gap-1 text-[9px] font-black text-green-600 uppercase tracking-widest">
                    <CheckCircle2 className="w-3 h-3" /> Connected
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type="password"
                  value={localKeys.harmonic}
                  onChange={(e) => setLocalKeys({ ...localKeys, harmonic: e.target.value })}
                  className="w-full p-4 pr-10 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-mono text-xs transition"
                  placeholder="Get yours at harmonic.ai"
                />
                {localKeys.harmonic && (
                  <button onClick={() => setLocalKeys({ ...localKeys, harmonic: "" })} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-lg transition text-gray-400">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <a href="https://harmonic.ai" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] font-bold text-red-600 hover:underline mt-1.5">
                <ExternalLink className="w-2.5 h-2.5" /> Get API key →
              </a>
            </div>
            <IntegrationField label="Tracxn" value={localKeys.tracxn} onChange={(v) => setLocalKeys({ ...localKeys, tracxn: v })} connected={tracxnConnected} onClear={() => setLocalKeys({ ...localKeys, tracxn: "" })} />
            <IntegrationField label="Dealroom" value={localKeys.dealroom} onChange={(v) => setLocalKeys({ ...localKeys, dealroom: v })} connected={dealroomConnected} onClear={() => setLocalKeys({ ...localKeys, dealroom: "" })} />
            <button onClick={saveKeys} className="w-full py-3 bg-gray-900 text-white font-black rounded-2xl text-xs uppercase tracking-widest hover:bg-black transition">Save Keys</button>
          </div>
          <div className="mt-6 p-4 bg-gray-50 rounded-2xl space-y-3">
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">How to get your keys</p>
            <a href="https://data.crunchbase.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-bold text-red-600 hover:underline"><ExternalLink className="w-3 h-3" /> Get Crunchbase API Key →</a>
            <a href="https://harmonic.ai" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-bold text-red-600 hover:underline"><ExternalLink className="w-3 h-3" /> Get Harmonic API Key →</a>
            <a href="https://tracxn.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-red-600 transition"><ExternalLink className="w-3 h-3" /> Get Tracxn API Key →</a>
            <a href="https://dealroom.co" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-red-600 transition"><ExternalLink className="w-3 h-3" /> Get Dealroom API Key →</a>
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="lg:col-span-2 space-y-4">

        {/* Mode Toggle */}
        <div className="flex gap-3">
          <button onClick={() => setMode("search")} className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest transition ${mode === "search" ? "bg-red-600 text-white" : "bg-white border border-gray-100 text-gray-400"}`}>
            <Search className="w-3.5 h-3.5" /> Search
          </button>
          <button onClick={() => setMode("thesis")} className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest transition ${mode === "thesis" ? "bg-red-600 text-white" : "bg-white border border-gray-100 text-gray-400"}`}>
            <Sparkles className="w-3.5 h-3.5" /> Thesis Matcher
          </button>
        </div>

        {/* SEARCH MODE */}
        {mode === "search" && (
          <>
            <div className="flex gap-3 flex-wrap">
              {[
                { id: "crunchbase", connected: crunchbaseConnected },
                { id: "harmonic", connected: harmonicConnected },
                { id: "tracxn", connected: tracxnConnected },
                { id: "dealroom", connected: dealroomConnected },
              ].map(({ id, connected }) => (
                <button key={id} onClick={() => { setActiveSource(id); setResults([]); setError(""); }} className={`px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest transition flex items-center gap-2 ${activeSource === id ? "bg-gray-900 text-white" : "bg-white border border-gray-100 text-gray-400"}`}>
                  {id}
                  {connected && <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>}
                </button>
              ))}
            </div>

            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
              <h3 className="text-xl font-black mb-6 tracking-tight">Market Intelligence Terminal</h3>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input type="text" value={researchQuery} onChange={(e) => setResearchQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} className="w-full pl-11 pr-4 py-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm border-2 border-transparent focus:border-red-600 transition" placeholder="Search startups, sectors, companies..." />
                </div>
                <button onClick={handleSearch} disabled={isSearching} className="px-6 py-4 bg-red-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest hover:bg-red-700 transition disabled:opacity-50">{isSearching ? "Searching..." : "Search"}</button>
              </div>
              {error && <div className="mt-4 flex items-center gap-3 p-4 bg-red-50 rounded-2xl border border-red-100"><AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" /><p className="text-sm font-bold text-red-600">{error}</p></div>}
            </div>

            {isSearching && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3 animate-pulse">
                    <div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-100 rounded-xl"></div><div className="space-y-2 flex-1"><div className="h-3 bg-gray-100 rounded-lg w-2/3"></div><div className="h-2 bg-gray-100 rounded-lg w-1/3"></div></div></div>
                    <div className="h-8 bg-gray-100 rounded-lg"></div>
                    <div className="grid grid-cols-3 gap-2"><div className="h-10 bg-gray-100 rounded-xl"></div><div className="h-10 bg-gray-100 rounded-xl"></div><div className="h-10 bg-gray-100 rounded-xl"></div></div>
                  </div>
                ))}
              </div>
            )}

            {!isSearching && results.length > 0 && (
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">{results.length} results for "{researchQuery}"</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.map((company, i) =>
                    resultsSource === "harmonic"
                      ? <HarmonicCard key={i} company={company} />
                      : <CompanyCard key={i} company={company} />
                  )}
                </div>
              </div>
            )}

            {!isSearching && results.length === 0 && !error && (
              <div className="bg-white rounded-3xl border border-gray-100 py-24 text-center">
                <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4"><Building2 className="w-7 h-7 text-gray-300" /></div>
                {activeSource === "harmonic" && !harmonicConnected ? (
                  <>
                    <p className="font-black text-gray-300 text-lg tracking-tight">Add your Harmonic API key above</p>
                    <p className="text-xs text-gray-300 font-bold mt-1">to search company intelligence</p>
                  </>
                ) : (
                  <>
                    <p className="font-black text-gray-300 text-lg tracking-tight">Search the startup universe</p>
                    <p className="text-xs text-gray-300 font-bold mt-1">Enter your API key and search any company or sector</p>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* THESIS MATCHER MODE */}
        {mode === "thesis" && (
          <>
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-5 h-5 text-red-600" />
                <div>
                  <h3 className="text-xl font-black tracking-tight">Thesis Matcher</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">AI ranks companies against your investment thesis</p>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Your Investment Thesis</label>
                <textarea
                  value={thesis}
                  onChange={(e) => setThesis(e.target.value)}
                  rows={5}
                  placeholder={`Example:\n\nWe invest in B2B SaaS companies at Seed or Series A stage in India. We look for technical founding teams, ARR above ₹2 Cr, strong NRR above 110%, and a clear enterprise go-to-market. We avoid consumer, deep tech, and biotech.`}
                  className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-red-600 rounded-2xl outline-none font-medium text-sm resize-none transition leading-relaxed"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Search Term for Companies</label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={thesisQuery}
                      onChange={(e) => setThesisQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleThesisMatch()}
                      placeholder="e.g. fintech India, B2B SaaS, enterprise software..."
                      className="w-full pl-11 pr-4 py-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm border-2 border-transparent focus:border-red-600 transition"
                    />
                  </div>
                  <button onClick={handleThesisMatch} disabled={isMatching} className="px-6 py-4 bg-red-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" />
                    {isMatching ? "Analysing..." : "Match"}
                  </button>
                </div>
              </div>

              {matchError && <div className="flex items-center gap-3 p-4 bg-red-50 rounded-2xl border border-red-100"><AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" /><p className="text-sm font-bold text-red-600">{matchError}</p></div>}

              {isMatching && matchStep && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <p className="text-sm font-bold text-blue-600">{matchStep}</p>
                </div>
              )}
            </div>

            {!isMatching && matchResults.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{matchResults.length} companies ranked by thesis match</p>
                  <div className="flex items-center gap-3 text-[9px] font-black uppercase tracking-widest">
                    <span className="flex items-center gap-1 text-green-600"><span className="w-2 h-2 bg-green-500 rounded-full"></span> 80%+ Strong</span>
                    <span className="flex items-center gap-1 text-yellow-600"><span className="w-2 h-2 bg-yellow-500 rounded-full"></span> 60%+ Okay</span>
                    <span className="flex items-center gap-1 text-red-500"><span className="w-2 h-2 bg-red-400 rounded-full"></span> Below 60%</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matchResults.map((item, i) => <MatchCard key={i} company={item.company} match={item.match} />)}
                </div>
              </div>
            )}

            {!isMatching && matchResults.length === 0 && !matchError && (
              <div className="bg-white rounded-3xl border border-gray-100 py-24 text-center">
                <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4"><FileText className="w-7 h-7 text-gray-300" /></div>
                <p className="font-black text-gray-300 text-lg tracking-tight">Paste your thesis above</p>
                <p className="text-xs text-gray-300 font-bold mt-1">AI will rank companies by how well they match your investment criteria</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
